import { useFlowMessages } from "@/api/messages";
import { useEffect, useMemo, useRef } from "react";
import type { AdvanceCondition } from "../types";

export function targetSelector(target: string): string {
  return `[data-onboarding-target="${target}"]`;
}

/** True once a user message equal to `prompt` exists in the session. */
export function usePromptSent(
  sessionId: string | null,
  prompt: string | null,
): boolean {
  const enabled = !!sessionId && !!prompt;
  const { data } = useFlowMessages(sessionId ?? "", undefined, { enabled });
  return useMemo(() => {
    if (!enabled || !data) return false;
    const wanted = prompt!.trim();
    return data.some((m) => m.origin === "user" && m.content.trim() === wanted);
  }, [data, enabled, prompt]);
}

interface AdvanceInput {
  condition: AdvanceCondition;
  /** Changes with the step; resets listeners and timers. */
  stepKey: string;
  promptSent: boolean;
  active: boolean;
  onAdvance: () => void;
}

/**
 * Evaluates the step's advance condition: the pinned prompt in the tour
 * session (passed in), a capture click on the anchor, or a timer.
 * `component` steps call `onAdvance` themselves.
 */
export function useAdvance({
  condition,
  stepKey,
  promptSent,
  active,
  onAdvance,
}: AdvanceInput) {
  const onAdvanceRef = useRef(onAdvance);
  useEffect(() => {
    onAdvanceRef.current = onAdvance;
  }, [onAdvance]);

  const met = active && condition.kind === "prompt-sent" && promptSent;
  useEffect(() => {
    if (met) onAdvanceRef.current();
  }, [met]);

  const target = condition.kind === "click" ? condition.target : null;
  useEffect(() => {
    if (!active || !target) return;
    const selector = targetSelector(target);
    const handler = (e: Event) => {
      const el = e.target as Element | null;
      if (el?.closest?.(selector)) onAdvanceRef.current();
    };
    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
  }, [active, target, stepKey]);

  const ms = condition.kind === "timer" ? condition.ms : null;
  useEffect(() => {
    if (!active || ms === null) return;
    const t = window.setTimeout(() => onAdvanceRef.current(), ms);
    return () => window.clearTimeout(t);
  }, [active, ms, stepKey]);
}
